<script setup lang="ts">
import { ref } from 'vue';

import { IconDocumentWord } from '@/components/design/icons';

const emit = defineEmits<{ download: [] }>();

const visible = ref(false);

function show(): void {
  visible.value = true;
}

function hide(): void {
  visible.value = false;
}

function handleDownload(): void {
  emit('download');
}

defineExpose({ show, hide });
</script>

<template>
  <NModal v-model:show="visible" :mask-closable="true" style="width: 560px">
    <div class="word-plugin-guide">
      <!-- ── Header ───────────────────────────────────────────────────── -->
      <div class="word-plugin-guide__header">
        <div class="word-plugin-guide__logo-wrap">
          <NIcon size="28" :color="'#2b579a'">
            <IconDocumentWord />
          </NIcon>
        </div>

        <div class="word-plugin-guide__header-text">
          <p class="word-plugin-guide__title">Install the Word Plugin</p>
          <p class="word-plugin-guide__subtitle">Side-load the GAIA add-in in your Word.</p>
        </div>
      </div>

      <!-- ── Body ────────────────────────────────────────────────────── -->
      <div class="word-plugin-guide__body">
        <p class="word-plugin-guide__intro">
          Download the manifest, then follow the platform-specific instructions below to load the
          add-in in Word.
        </p>

        <div class="word-plugin-guide__step">
          <p class="word-plugin-guide__step-title">Mac</p>
          <p class="word-plugin-guide__step-text">
            Copy the manifest into Word's <code>wef</code> folder, then enable it from the
            <em>Insert &gt; Add-ins &gt; My Add-ins</em> menu.
          </p>
          <a
            class="word-plugin-guide__step-link"
            href="https://learn.microsoft.com/en-us/office/dev/add-ins/testing/sideload-an-office-add-in-on-mac"
            target="_blank"
            rel="noopener noreferrer"
          >
            Microsoft Learn: Side-load on Mac →
          </a>
        </div>

        <div class="word-plugin-guide__step">
          <p class="word-plugin-guide__step-title">Windows</p>
          <p class="word-plugin-guide__step-text">
            Place the manifest in a shared folder catalog and trust the location in Word's Trust
            Center, then enable the add-in.
          </p>
          <a
            class="word-plugin-guide__step-link"
            href="https://learn.microsoft.com/en-us/office/dev/add-ins/testing/create-a-network-shared-folder-catalog-for-task-pane-and-content-add-ins"
            target="_blank"
            rel="noopener noreferrer"
          >
            Microsoft Learn: Side-load on Windows →
          </a>
        </div>

        <div class="word-plugin-guide__step">
          <p class="word-plugin-guide__step-title">Office on the web</p>
          <p class="word-plugin-guide__step-text">
            Open Word online, then go to <em>Insert &gt; Add-ins &gt; Upload My Add-in</em> and
            select the downloaded manifest file.
          </p>
          <a
            class="word-plugin-guide__step-link"
            href="https://learn.microsoft.com/en-us/office/dev/add-ins/testing/sideload-office-add-ins-for-testing"
            target="_blank"
            rel="noopener noreferrer"
          >
            Microsoft Learn: Side-load on the web →
          </a>
        </div>
      </div>

      <!-- ── Footer ──────────────────────────────────────────────────── -->
      <div class="word-plugin-guide__footer">
        <NButton @click="hide">Close</NButton>
        <NButton type="primary" @click="handleDownload">Download manifest</NButton>
      </div>
    </div>
  </NModal>
</template>

<style scoped>
.word-plugin-guide {
  background: var(--n-color);
  border-radius: 12px;
  overflow: hidden;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.18),
    0 4px 16px rgba(0, 0, 0, 0.1);
}

.word-plugin-guide__header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 28px 22px;
  border-bottom: 1px solid var(--n-border-color);
  background: linear-gradient(135deg, var(--n-color) 0%, var(--n-color-modal) 100%);
}

.word-plugin-guide__logo-wrap {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  background: var(--n-color-modal);
  border: 1px solid var(--n-border-color);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.word-plugin-guide__title {
  margin: 0 0 -2px;
  font-size: 16px;
  font-weight: 700;
  color: var(--n-text-color-1);
  letter-spacing: -0.01em;
}

.word-plugin-guide__subtitle {
  margin: 0;
  font-size: 13px;
  color: var(--n-text-color-3);
}

.word-plugin-guide__body {
  padding: 22px 28px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.word-plugin-guide__intro {
  margin: 0;
  font-size: 13.5px;
  line-height: 1.65;
  color: var(--n-text-color-2);
}

.word-plugin-guide__step {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
  background: var(--n-color-modal);
  border: 1px solid var(--n-border-color);
  border-radius: 8px;
}

.word-plugin-guide__step-title {
  margin: 0;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--n-text-color-1);
}

.word-plugin-guide__step-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--n-text-color-2);
}

.word-plugin-guide__step-text code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12.5px;
  padding: 1px 5px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
}

.word-plugin-guide__step-link {
  margin-top: 4px;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--n-primary-color);
  text-decoration: none;
}

.word-plugin-guide__step-link:hover {
  text-decoration: underline;
}

.word-plugin-guide__footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 16px 28px 22px;
  border-top: 1px solid var(--n-border-color);
}
</style>
